import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Card, CardBody, Col, Container, Label, Row } from 'reactstrap';
import { useUserContext } from '../../../context/userProvider';
import { SourceService, UserService } from '../../../services';

const NOTIFICATION_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every 1 hour' },
  { value: 120, label: 'Every 2 hours' },
  { value: 240, label: 'Every 4 hours' },
  { value: 480, label: 'Every 8 hours' },
  { value: 1440, label: 'Once a day' },
];

const PageSettings = () => {
  const { currentUser, setCurrentUser } = useUserContext();

  const [backgroundColor, setBackgroundColor] = useState(
    currentUser?.background_color ?? null,
  );
  const [textColor, setTextColor] = useState(
    currentUser?.text_color ?? '#8d29da',
  );
  const [underline, setUnderline] = useState(currentUser?.underline ?? false);
  const [language, setLanguage] = useState(currentUser?.primary_language_id);
  const [notificationInterval, setNotificationInterval] = useState(
    currentUser?.notification_interval ?? 0,
  );

  const { data } = useQuery('sourceData', SourceService.getSourceData, {
    retry: false,
  });

  const { mutate, isLoading, isSuccess } = useMutation(UserService.profileUpdate, {
    onSuccess: (data) => {
      if (data) setCurrentUser(data);
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    mutate({
      primary_language_id: language,
      background_color: backgroundColor,
      text_color: textColor,
      underline,
      notification_interval: Number(notificationInterval),
    });
  };

  return (
    <section className="section p-0 mt-4">
      <Container>
        <Card className="border-0 rounded shadow-sm">
          <CardBody className="p-4">
            <form onSubmit={onSubmit}>
              {/* Profile info */}
              <h5 className="mb-3">Profile</h5>
              <Row className="mb-4">
                <Col lg={6}>
                  <div className="mb-3">
                    <Label className="form-label text-muted small">Email</Label>
                    <p className="mb-0 fw-semibold">{currentUser?.email}</p>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="mb-3">
                    <Label className="form-label text-muted small">Account type</Label>
                    <p className="mb-0">
                      <span className={`badge ${currentUser?.subscribed ? 'bg-warning text-dark' : 'bg-light text-muted border'}`}>
                        {currentUser?.subscribed ? 'Premium' : 'Free'}
                      </span>
                    </p>
                  </div>
                </Col>
              </Row>

              {/* Language */}
              <h5 className="mb-3">Language</h5>
              <Row className="mb-4">
                <Col lg={6}>
                  <div className="mb-3">
                    <Label className="form-label">Your native language</Label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="form-select"
                    >
                      {(data?.sourceLanguages || []).map((i) => (
                        <option key={i.id} value={i.id}>{i.language}</option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>

              {/* Notification */}
              <h5 className="mb-3">Word Reminder</h5>
              <Row className="mb-4">
                <Col lg={6}>
                  <div className="mb-3">
                    <Label className="form-label">Show reminder popup while browsing</Label>
                    <select
                      value={notificationInterval}
                      onChange={(e) => setNotificationInterval(e.target.value)}
                      className="form-select"
                    >
                      {NOTIFICATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-muted small mt-1">
                      A popup will appear on web pages to remind you to practice your saved words.
                    </p>
                  </div>
                </Col>
              </Row>

              {/* Highlight style */}
              <h5 className="mb-3">Highlight Style</h5>
              <Row className="mb-2">
                <Col lg={4}>
                  <div className="mb-3">
                    <Label className="form-label d-flex align-items-center gap-2">
                      Background Color
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={backgroundColor !== null}
                          onChange={() =>
                            setBackgroundColor(backgroundColor === null ? '#ffffff' : null)
                          }
                        />
                        {backgroundColor === null && (
                          <label className="form-check-label text-muted small">None</label>
                        )}
                      </div>
                    </Label>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      style={backgroundColor === null ? { opacity: 0.2, pointerEvents: 'none' } : {}}
                      value={backgroundColor ?? '#ffffff'}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="mb-3">
                    <Label className="form-label">Text Color</Label>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="mb-3">
                    <Label className="form-label">Underline</Label>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="underlineCheck"
                        checked={underline}
                        onChange={(e) => setUnderline(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="underlineCheck">
                        Underline highlighted words
                      </label>
                    </div>
                  </div>
                </Col>
                <Col xs={12} className="mb-4">
                  <p className="text-muted small mb-1">Preview:</p>
                  <span
                    style={{
                      color: textColor,
                      backgroundColor: backgroundColor ?? 'transparent',
                      textDecoration: underline ? 'underline' : 'none',
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    example word
                  </span>
                </Col>
              </Row>

              <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={isLoading}>
                {isLoading ? 'Saving...' : isSuccess ? 'Saved ✓' : 'Save Changes'}
              </button>
            </form>
          </CardBody>
        </Card>
      </Container>
    </section>
  );
};

export default PageSettings;
